// MIDAS V3 — "Attach chat" multi-thread view state (MODEL-PICKER spec §6)
//
// Up to 2 threads attach ALONGSIDE the primary chat as fully live, side-by-
// side columns. This slice owns the attached columns' identity + their full
// message ARRAYS (the one thing MIDAS-SCOPESTREAM doesn't provide — stream
// buffers in chatBotSlice.threadBuffers are already per-thread and are reused
// verbatim for per-column streaming display).
//
// Design decisions (from MODEL_PICKER_SPEC.md §6 + design_handoff_multichat_v33):
//   • columns: max 2, ORDER = attach order. Primary chat is NOT in here — it
//     stays the existing chatBot.messages / midasPage.activeThreadId surface.
//   • Message writes for attached columns are dispatched FROM the sendMessage
//     thunk (chatBotSlice) via `appendColumnMessage` — the thunk already owns
//     blob-URL creation and bot-message construction, so routing from there
//     avoids forking either (see the deviation note in chatBotSlice).
//   • pendingBotMessage dedup: when the thunk delivers a bot message to a
//     column it flags `deliveredToColumn` on its payload; chatBotSlice's
//     fulfilled reducer then clears the buffer WITHOUT storing
//     pendingBotMessage — so a later loadThread of that thread can never
//     double-append the answer.
//   • Detach does NOT abort an in-flight stream — the stream continues as a
//     normal background stream (MIDAS-SCOPESTREAM) and lands in
//     threadBuffers[threadId].pendingBotMessage.
//   • Rail behaviour (design handoff): clicking an ATTACHED row focuses its
//     column (never navigation); clicking any other row swaps the PRIMARY
//     in place. The loadThread.pending auto-detach below is the safety net
//     for paths that CAN make an attached thread primary (e.g. the
//     /midas/history page) — one thread never renders in two columns.
//   • Attachment persistence: localStorage, keyed per user id when auth state
//     is available at write time (design open-Q 3) — restored on mount by
//     MidasChatAreaV3 via restoreAttachedThreads().
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ChatMessage } from "../../shared/chatBot/models/chatBotModels";
import chatBotApi from "../../shared/chatBot/ChatBotApi";
import {
  deleteThread,
  loadThread,
  mapThreadDetailMessages,
  renameThread,
  closeArtifactPanel,
} from "./midasPageSlice";

// ============================================================
// Types
// ============================================================

export const MAX_ATTACHED_COLUMNS = 2;

export interface AttachedColumn {
  threadId: string;
  title: string;
  /** From thread detail — passed EXPLICITLY on column sends so the global
   *  machineContext (which belongs to the PRIMARY chat) never bleeds in. */
  machineId: string | null;
  messages: ChatMessage[];
  /** Thread detail fetch in-flight. */
  loading: boolean;
  /** Thread detail fetch error (i18n-keyed by the column component). */
  error: string | null;
}

export interface MidasAttachState {
  /** Max 2, order = attach order. */
  columns: AttachedColumn[];
  /** Which column's artifact opened the pane; null = primary chat. While the
   *  pane is open only the source column renders (design: siblings collapse). */
  artifactSourceThreadId: string | null;
  /** localStorage key suffix captured from auth state at last attach — lets
   *  the auto-detach extraReducers (which can't read other slices) persist. */
  persistKey: string | null;
}

// ============================================================
// localStorage persistence (design open-Q 3: per-user when available)
// ============================================================

const LS_ATTACHED_PREFIX = "midas.v3.attachedThreads.";

const safeLocalStorage = (): Storage | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const persistAttached = (persistKey: string | null, ids: string[]): void => {
  const ls = safeLocalStorage();
  if (!ls || !persistKey) return;
  try {
    ls.setItem(LS_ATTACHED_PREFIX + persistKey, JSON.stringify(ids));
  } catch {
    // quota / private mode — non-fatal
  }
};

const readAttached = (persistKey: string): string[] => {
  const ls = safeLocalStorage();
  if (!ls) return [];
  try {
    const raw = ls.getItem(LS_ATTACHED_PREFIX + persistKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === "string").slice(0, MAX_ATTACHED_COLUMNS)
      : [];
  } catch {
    return [];
  }
};

/** User-scoped persistence key from auth state; falls back to per-browser. */
const resolvePersistKey = (state: any): string => {
  const uid = state?.account?.user?.id ?? state?.account?.user?.userId;
  return typeof uid === "string" && uid ? uid : "anon";
};

// ============================================================
// Thunks
// ============================================================

/** Attach a thread as a live column. Guards (condition): not the active
 *  thread, not already attached, under the 2-column cap. Pushes a placeholder
 *  immediately (pending), fetches the thread detail, maps its messages with
 *  the SAME mapper loadThread uses (mapThreadDetailMessages — one source of
 *  truth), and fills the column (fulfilled). */
export const attachThread = createAsyncThunk(
  "midasAttach/attachThread",
  async (
    args: { threadId: string; title?: string; machineId?: string | null },
    { getState }
  ) => {
    const detail = await chatBotApi.getThread(args.threadId);
    const messages = mapThreadDetailMessages(detail);
    return {
      threadId: args.threadId,
      title: detail.title || args.title || "",
      machineId: detail.machine_id ?? null,
      messages,
      persistKey: resolvePersistKey(getState()),
    };
  },
  {
    condition: (args, { getState }) => {
      const s = getState() as any;
      const cols: AttachedColumn[] = s.midasAttach?.columns ?? [];
      if (cols.length >= MAX_ATTACHED_COLUMNS) return false;
      if (cols.some((c) => c.threadId === args.threadId)) return false;
      if (s.midasPage?.activeThreadId === args.threadId) return false;
      return true;
    },
  }
);

/** Detach a column. Closes the artifact pane first when the detached column
 *  is the pane's source (spec §6: "close the pane first, then detach").
 *  Does NOT abort an in-flight stream — it continues as a background stream. */
export const detachThread = createAsyncThunk(
  "midasAttach/detachThread",
  async (threadId: string, { dispatch, getState }) => {
    const s = getState() as any;
    const sourceId: string | null = s.midasAttach?.artifactSourceThreadId ?? null;
    if (sourceId === threadId && s.midasPage?.artifactPanelOpen) {
      dispatch(closeArtifactPanel());
    }
    return { threadId, persistKey: resolvePersistKey(getState()) };
  }
);

/** Restore persisted attachments on mount (MidasChatAreaV3). Caller gates on
 *  isCcDevSurface() + desktop tier — same gate as the rail menu item. */
export const restoreAttachedThreads = createAsyncThunk(
  "midasAttach/restoreAttachedThreads",
  async (_: void, { dispatch, getState }) => {
    const s = getState() as any;
    if ((s.midasAttach?.columns ?? []).length > 0) return;
    const key = resolvePersistKey(s);
    const ids = readAttached(key);
    for (const id of ids) {
      dispatch(attachThread({ threadId: id }));
    }
  }
);

// ============================================================
// Slice
// ============================================================

const initialState: MidasAttachState = {
  columns: [],
  artifactSourceThreadId: null,
  persistKey: null,
};

const removeColumn = (state: MidasAttachState, threadId: string): void => {
  const before = state.columns.length;
  state.columns = state.columns.filter((c) => c.threadId !== threadId);
  if (state.columns.length !== before) {
    if (state.artifactSourceThreadId === threadId) {
      state.artifactSourceThreadId = null;
    }
    persistAttached(
      state.persistKey,
      state.columns.map((c) => c.threadId)
    );
  }
};

const midasAttachSlice = createSlice({
  name: "midasAttach",
  initialState,
  reducers: {
    /** Which column's artifact opened the pane (null = primary). Dispatched
     *  alongside openArtifact / openArtifactAtPage from column subtrees via
     *  AttachColumnContext. */
    setArtifactSourceThread: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.artifactSourceThreadId = action.payload;
    },
    /** Append a user/bot ChatMessage to an attached column. Dispatched from
     *  the sendMessage thunk (chatBotSlice) for column-origin sends. No-op if
     *  the column was detached mid-flight (the message then lands via the
     *  normal pendingBotMessage background path instead). */
    appendColumnMessage: (
      state,
      action: PayloadAction<{ threadId: string; message: ChatMessage }>
    ) => {
      const col = state.columns.find(
        (c) => c.threadId === action.payload.threadId
      );
      if (col) {
        col.messages.push(action.payload.message);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ── attachThread lifecycle ──
      .addCase(attachThread.pending, (state, action) => {
        const { threadId, title, machineId } = action.meta.arg;
        // condition() already guarded, but re-check defensively (double
        // dispatch in the same tick can slip past condition snapshots).
        if (
          state.columns.length >= MAX_ATTACHED_COLUMNS ||
          state.columns.some((c) => c.threadId === threadId)
        ) {
          return;
        }
        state.columns.push({
          threadId,
          title: title || "",
          machineId: machineId ?? null,
          messages: [],
          loading: true,
          error: null,
        });
      })
      .addCase(attachThread.fulfilled, (state, action) => {
        const p = action.payload;
        state.persistKey = p.persistKey;
        const col = state.columns.find((c) => c.threadId === p.threadId);
        if (!col) return; // detached while loading
        col.title = p.title;
        col.machineId = p.machineId;
        col.messages = p.messages;
        col.loading = false;
        col.error = null;
        persistAttached(
          state.persistKey,
          state.columns.map((c) => c.threadId)
        );
      })
      .addCase(attachThread.rejected, (state, action) => {
        const { threadId } = action.meta.arg;
        const col = state.columns.find((c) => c.threadId === threadId);
        if (!col) return;
        col.loading = false;
        col.error = action.error?.message || "load_failed";
      })
      // ── detachThread ──
      .addCase(detachThread.fulfilled, (state, action) => {
        state.persistKey = action.payload.persistKey;
        removeColumn(state, action.payload.threadId);
      })
      // ── Consistency: one thread never renders in two columns. The rail
      //    never navigates to an attached thread (it focuses the column), but
      //    the /midas/history page CAN loadThread an attached one — auto-
      //    detach its column the moment it becomes primary. ──
      .addCase(loadThread.pending, (state, action) => {
        removeColumn(state, action.meta.arg);
      })
      // ── Delete of an attached thread → auto-detach (design handoff). ──
      .addCase(deleteThread.fulfilled, (state, action) => {
        removeColumn(state, action.payload);
      })
      // ── Rename of an attached thread → live column-header update. ──
      .addCase(renameThread.pending, (state, action) => {
        const { id, title } = action.meta.arg;
        const col = state.columns.find((c) => c.threadId === id);
        if (col) col.title = title;
      })
      // ── Pane closed → clear the source so a stale value can never hide
      //    columns on a later artifact open from an un-instrumented site. ──
      .addCase(closeArtifactPanel, (state) => {
        state.artifactSourceThreadId = null;
      });
  },
});

export const { setArtifactSourceThread, appendColumnMessage } =
  midasAttachSlice.actions;

// ============================================================
// Selectors (draft guard — spec §6 HARD REQUIREMENT)
// ============================================================

/** True while ANY attached column has a stream in flight. */
export const selectAnyColumnStreaming = (state: any): boolean => {
  const cols: AttachedColumn[] = state.midasAttach?.columns ?? [];
  return cols.some(
    (c) => state.chatBot?.threadBuffers?.[c.threadId]?.isLoading === true
  );
};

/** True while a PRIMARY DRAFT first turn (no thread_id on the wire yet) is
 *  in flight — i.e. an optimistic thread exists whose buffer is streaming and
 *  `thread_created` hasn't reconciled it to a real id yet. */
export const selectDraftStreamInFlight = (state: any): boolean => {
  const threads: Array<{ id: string; isOptimistic?: boolean }> =
    state.midasPage?.threads ?? [];
  return threads.some(
    (t) =>
      t.isOptimistic === true &&
      state.chatBot?.threadBuffers?.[t.id]?.isLoading === true
  );
};

export default midasAttachSlice.reducer;
